import { useEffect, useMemo, useRef, useState } from "react"
import { api, errorText } from "../api/client"
import type { ProjectCard } from "../api/types"
import { age } from "../lib/time"
import { openProject } from "../store/actions"
import { setUi, useStore } from "../store/store"
import { Glyph } from "./ConnectPanel"
import { PersonSwatch } from "./ui"

/**
 * The page before any project: one card per project with what matters at a
 * glance — who is in it, what it is connected to, how many agents are working,
 * what is blocked or failing, and how busy it has been today.
 */
export function ProjectsHome() {
	const [cards, setCards] = useState<ProjectCard[] | null>(null)
	const [err, setErr] = useState<string | null>(null)
	const pulse = useStore(s => s.pulse)
	const me = useStore(s => s.me)
	const now = useStore(s => s.now)

	const load = useRef<() => void>(() => undefined)
	load.current = () =>
		api
			.overview()
			.then(r => (setCards(r.projects), setErr(null)))
			.catch(e => setErr(errorText(e)))

	useEffect(() => {
		load.current()
		const t = setInterval(
			() => document.visibilityState === "visible" && load.current(),
			20_000,
		)
		return () => clearInterval(t)
	}, [])
	// Something happened in a project: refresh, at most every 1.5 s.
	useEffect(() => {
		if (!pulse) return
		const t = setTimeout(() => load.current(), 1500)
		return () => clearTimeout(t)
	}, [pulse])
	// A project created or renamed elsewhere on this page.
	useEffect(() => {
		if (cards) load.current()
	}, [me])

	const totals = useMemo(() => {
		const t = { working: 0, idle: 0, blockers: 0, open: 0, failing: 0, day: 0 }
		for (const c of cards ?? []) {
			t.working += c.agents.working
			t.idle += c.agents.idle
			t.blockers += c.requests.blockers
			t.open += c.requests.open
			t.failing += c.failing
			t.day += c.activity.day
		}
		return t
	}, [cards])

	const sorted = useMemo(
		() => [...(cards ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
		[cards],
	)

	return (
		<main className="home">
			<div className="home-h">
				<h1>Projects</h1>
				{cards && (
					<div className="home-totals" aria-label="Across all projects">
						<span>
							<b>{cards.length}</b> projects
						</span>
						<span>
							<span className="sdot s-active" /> <b>{totals.working}</b> working
						</span>
						<span>
							<span className="sdot s-idle" /> <b>{totals.idle}</b> idle
						</span>
						<span className={totals.blockers ? "hot" : ""}>
							<b>{totals.blockers}</b> blockers
						</span>
						<span>
							<b>{totals.open}</b> open requests
						</span>
						<span className={totals.failing ? "bad" : ""}>
							<b>{totals.failing}</b> failing
						</span>
						<span>
							<b>{totals.day}</b> messages today
						</span>
					</div>
				)}
				<span className="grow" />
				<button className="btn btn-primary" onClick={() => setUi({ newProject: true })}>
					+ New project
				</button>
			</div>
			{err && !cards && <div className="form-error">{err}</div>}
			{!cards && !err && <div className="loading-bar" />}
			<div className="cards">
				{sorted.map(c => (
					<Card key={c.id} c={c} meId={me?.user.id} now={now} />
				))}
				{cards && (
					<button className="pcard pcard-new" onClick={() => setUi({ newProject: true })}>
						<span className="plus">+</span>
						New project
					</button>
				)}
			</div>
		</main>
	)
}

function Card({ c, meId, now }: { c: ProjectCard; meId?: string; now: number }) {
	const live = c.agents.working + c.agents.idle
	const agentsTotal = live + c.agents.offline
	const repo = c.githubRepos[0]
	const hot = c.requests.blockers > 0
	const bad = c.failing > 0
	const people = [...c.people].sort(
		(a, b) => +(b.userId === meId) - +(a.userId === meId) || b.chats - a.chats,
	)
	const open = () => void openProject(c.id)
	return (
		<article
			className={"pcard" + (hot ? " hot" : "") + (bad ? " bad" : "")}
			role="link"
			tabIndex={0}
			aria-label={`Open ${c.name}`}
			onClick={open}
			onKeyDown={e =>
				(e.key === "Enter" || e.key === " ") && (e.preventDefault(), open())
			}
		>
			<header className="pcard-h">
				<b className="ell">{c.name}</b>
				{c.agents.working > 0 ? (
					<span className="badge-live">
						<span className="sdot s-active" /> {c.agents.working} working
					</span>
				) : live > 0 ? (
					<span className="badge-idle">
						<span className="sdot s-idle" /> idle
					</span>
				) : (
					<span className="badge-quiet">quiet</span>
				)}
			</header>
			{/* Always one line, so every card's rows line up. */}
			<div className="pcard-desc ell">{c.description || "\u00a0"}</div>

			<div className="pcard-conn" aria-label="Connections">
				{repo ? (
					<span
						className="conn repo"
						title={
							c.githubLastEventAt
								? `GitHub connected — last event ${age(c.githubLastEventAt, now)} ago`
								: "Repo linked, but GitHub isn't sending events yet"
						}
					>
						<GithubMark />
						<span className="mono ell">{repo}</span>
						<span className={"repo-dot" + (c.githubLastEventAt ? " on" : "")} />
					</span>
				) : (
					<span className="conn repo none">
						<GithubMark /> no repo
					</span>
				)}
				<span
					className={"conn app" + (c.agents.claude ? "" : " none")}
					title={`${c.agents.claude} Claude ${c.agents.claude === 1 ? "chat" : "chats"} online`}
					aria-label={`${c.agents.claude} Claude chats online`}
				>
					<Glyph app="claude-code" />
					{c.agents.claude}
				</span>
				<span
					className={"conn app" + (c.agents.codex ? "" : " none")}
					title={`${c.agents.codex} Codex ${c.agents.codex === 1 ? "chat" : "chats"} online`}
					aria-label={`${c.agents.codex} Codex chats online`}
				>
					<Glyph app="codex" />
					{c.agents.codex}
				</span>
			</div>

			<div className="pcard-stats">
				<Stat
					n={c.agents.working}
					label="working"
					sub={`${c.agents.idle} idle · ${c.agents.offline} off`}
					tone={c.agents.working ? "ok" : undefined}
				/>
				<Stat
					n={c.requests.blockers}
					label="blockers"
					sub={
						c.requests.oldestBlockerAt
							? `oldest ${age(c.requests.oldestBlockerAt, now)}`
							: "none"
					}
					tone={hot ? "hot" : undefined}
				/>
				<Stat
					n={c.requests.open}
					label="open"
					sub={`${c.requests.unclaimed} unclaimed`}
					tone={c.requests.unclaimed ? "warn" : undefined}
				/>
				<Stat
					n={c.failing}
					label="failing"
					sub="deliveries"
					tone={bad ? "bad" : undefined}
				/>
			</div>

			<div className="pcard-load" title="Messages per hour, last 24 hours">
				<Spark hours={c.activity.hours} />
				<span className="load-n">
					<b>{c.activity.lastHour}</b>/h · {c.activity.day} today
				</span>
			</div>

			<footer className="pcard-f">
				<span className="people-stack">
					{people.slice(0, 6).map(p => (
						<span
							key={p.userId}
							className={p.chats ? "on" : ""}
							title={`${p.handle}${p.chats ? ` — ${p.chats} chats online` : ""}`}
						>
							<PersonSwatch handle={p.handle} />
						</span>
					))}
					{people.length > 6 && <span className="more">+{people.length - 6}</span>}
				</span>
				<span className="dim ell">
					{people.length} {people.length === 1 ? "person" : "people"} · {agentsTotal}{" "}
					{agentsTotal === 1 ? "agent" : "agents"}
				</span>
				<span className="grow" />
				<span className="dim">
					{c.lastActivityAt
						? `active ${age(c.lastActivityAt, now)} ago`
						: "no messages yet"}
				</span>
			</footer>
		</article>
	)
}

function Stat(props: {
	n: number
	label: string
	sub: string
	tone?: "ok" | "hot" | "warn" | "bad"
}) {
	return (
		<div className={"pstat" + (props.tone ? " " + props.tone : "")}>
			<b>{props.n}</b>
			<span className="pstat-l">{props.label}</span>
			<span className="pstat-s ell">{props.sub}</span>
		</div>
	)
}

/** 24 hourly bars, the current hour on the right. */
function Spark({ hours }: { hours: number[] }) {
	// A floor so a quiet day reads as quiet, not as a wall of full bars.
	const max = Math.max(8, ...hours)
	return (
		<svg className="spark" viewBox="0 0 96 18" preserveAspectRatio="none" aria-hidden>
			{hours.map((n, i) => {
				const h = n ? Math.max(1.5, (n / max) * 18) : 0.6
				return (
					<rect
						key={i}
						x={i * 4}
						y={18 - h}
						width={3}
						height={h}
						rx={0.6}
						className={i === hours.length - 1 ? "now" : n ? "on" : "off"}
					/>
				)
			})}
		</svg>
	)
}

function GithubMark() {
	return (
		<svg className="gh-mark" viewBox="0 0 16 16" width="11" height="11" aria-hidden>
			<path
				fill="currentColor"
				d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"
			/>
		</svg>
	)
}
